import {Component, ElementRef, Input, OnInit, ViewChild} from "@angular/core";
import {TranslatableComponent} from "../../../core/models/TranslatableComponent";
import {TranslateService, TranslatePipe} from "@ngx-translate/core";
import {ApplicationUser} from "../../../core/models/ApplicationUser";
import {AuthenticationService} from "../../../core/services/services/authentication.service";
import {Preference, PreferencesService} from "../../../core/services/services/preferences.service";
import {DropDownChoice} from "../../employee-operations/abstract-employee-details.component";
import MarketUnit from "../../../core/config/MarketUnit";
import {EmployeeService} from "../../../core/services/services/employee.service";
import {DivisionService} from "../../../core/services/services/division.service";
import {HomescreenService} from "../../../core/services/services/homescreen.service";
import {WorkloadOverviewCategory} from "../../../core/models/Enums/WorkloadOverviewCategory";
import {Observable} from "rxjs";
import {CustomizingService} from "../../../core/services/services/customizing.service";
import {FormControl, ReactiveFormsModule, FormsModule} from "@angular/forms";
import {TooltipPosition, MatTooltip} from "@angular/material/tooltip";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatSelect} from "@angular/material/select";
import {MatOption} from "@angular/material/autocomplete";
import {NgClass, DecimalPipe, DatePipe} from "@angular/common";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {TrafficLightMecMepComponent} from "../traffic-light-mec-mep/traffic-light-mec-mep.component";
import {MatIcon} from "@angular/material/icon";

@Component({
	selector: "app-mec-data",
	templateUrl: "./mec-data.component.html",
	styleUrls: ["./mec-data.component.scss"],
	imports: [
		MatFormField,
		MatLabel,
		MatInput,
		MatSelect,
		ReactiveFormsModule,
		FormsModule,
		MatOption,
		NgClass,
		MatProgressSpinner,
		TrafficLightMecMepComponent,
		MatIcon,
		MatTooltip,
		TranslatePipe,
		DecimalPipe,
		DatePipe,
	],
})
export class MecDataComponent extends TranslatableComponent implements OnInit {
	tooltipPositionOptions: TooltipPosition[] = ["after", "before", "above", "below", "left", "right"];
	tooltipPosition = new FormControl(this.tooltipPositionOptions[0]);
	showWorkloadChart: boolean = false;
	isDivisionManager = false;
	isAdminOrGBL = false;
	isAdmin = false;
	departmentManager = false;
	noDivision = false;
	divisionOptions: DropDownChoice<string>[];
	familyGroup: DropDownChoice<string>[] = [
		{value: "M_B", viewValue: "Management + Business"},
		{
			value: "B",
			viewValue: "Business",
		},
	];
	selectedDivision: string;
	selectedDivisionLabel: string;
	selectedBusinessUnit: string;
	selectedFamilyGroup = "M_B";
	businessUnitOptions: DropDownChoice<string>[];
	lastImportDate: Date;
	MECWorkload: number;
	MECQuality: number;
	MEPWorkloadPreviousMonth: number;
	MEPWorkloadCMEO: number;
	MEPWorkloadCMSO: number;
	MEPWorkloadNMEO: number;
	MEPWorkloadNMSO: number;
	loading = true;
	loadingNextMonth = false;
	hasValidMecData: boolean;
	shakingArrow: "previous" | "next" | null = null;
	previousMonth: number;
	currentMonth: number;
	nextMonth: number;
	monthOffset = 0;
	readonly maxMonthOffset = 5;
	displayedMonthLabel = "";
	selectedCategory: WorkloadOverviewCategory[] = [WorkloadOverviewCategory.OPERATIV];
	isJanuary = new Date().getMonth() === 0;
	canViewCockpit = false;
	division: string;

	@Input() user: ApplicationUser;

	@Input() cockpitBusinessUnits: string;

	constructor(
		private translateService: TranslateService,
		public loginService: AuthenticationService,
		private preferencesService: PreferencesService,
		private _employeeService: EmployeeService,
		private divisionService: DivisionService,
		private homescreenService: HomescreenService,
		private customizingService: CustomizingService,
	) {
		super(translateService, "MecDataComponent");
	}

	async ngOnInit() {
		this.getUserRoles();
		this.setDates();

		if (this.isWorthy()) {
			await this.setupMECCockpit(this.user);
		}
	}

	private async setupMECCockpit(user: ApplicationUser) {
		if (this.cockpitBusinessUnits !== "" && this.cockpitBusinessUnits !== undefined) {
			this.noDivision = false;
			this.getPreferences(user);
			await this.getBusinessUnitOptions();
			await this.getDivisionOptions(this.selectedBusinessUnit, false);
			if (this.divisionOptions != null) {
				await this.reloadWorkload(this.selectedDivision, this.selectedFamilyGroup).then(() => (this.loading = false));
			}
		}
	}

	async reloadWorkload(selectedDivision, selectedFamilyGroup) {
		this.showWorkloadChart = false;
		this.noDivision = false;
		this.loading = true;
		this.putPreferences();
		this.getLastImportDate().subscribe((d) => {
			if (d !== null) {
				this.lastImportDate = new Date(d);
				let month = this.lastImportDate.getMonth() + 1;
				this.hasValidMecData = month >= this.currentMonth;
			} else {
				this.lastImportDate = null;
			}
		});
		this.division = await this.divisionService.getDivisionLabel(this.selectedDivision);
		if (!this.isJanuary) {
			this.getMECData(this.selectedBusinessUnit, selectedDivision, selectedFamilyGroup, false).subscribe(
				(MECWorkload) => (this.MECWorkload = MECWorkload),
			);
			this.getMECData(this.selectedBusinessUnit, selectedDivision, selectedFamilyGroup, true).subscribe(
				(MECBookingQuality) => (this.MECQuality = MECBookingQuality),
			);
		}
		await this.getMEPWorkload(this.selectedBusinessUnit, selectedDivision, selectedFamilyGroup);

		this.loading = false;
	}

	private async getMEPWorkload(businessUnit, selectedDivision, selectedFamilyGroup, mappings?: {month: number; type: string; property: string}[]) {
		const workloadMappings = mappings ?? [
			{month: this.previousMonth, type: "expectedOperative", property: "MEPWorkloadPreviousMonth"},
			{month: this.currentMonth, type: "expectedOperative", property: "MEPWorkloadCMEO"},
			{month: this.currentMonth, type: "safeOperative", property: "MEPWorkloadCMSO"},
			{month: this.nextMonth, type: "expectedOperative", property: "MEPWorkloadNMEO"},
			{month: this.nextMonth, type: "safeOperative", property: "MEPWorkloadNMSO"},
		];

		const promises = workloadMappings.map(async (mapping) => {
			const workload = await this.homescreenService
				.getMEPData(businessUnit, selectedDivision, selectedFamilyGroup, mapping.month, mapping.type)
				.toPromise();
			return (this[mapping.property] = workload);
		});

		await Promise.all(promises);
	}

	private setDates() {
		this.previousMonth = new Date().getMonth();
		this.currentMonth = this.previousMonth + 1;
		this.nextMonth = this.currentMonth + 1 + this.monthOffset;
		const displayedDate = new Date(new Date().getFullYear(), this.nextMonth - 1, 1);
		const lang = this.translateService.currentLang || this.translateService.defaultLang || "de";
		this.displayedMonthLabel = displayedDate.toLocaleDateString(lang, {month: "long"});
	}

	async changeMonth(delta: number) {
		const newOffset = this.monthOffset + delta;
		if (newOffset < 0 || newOffset > this.maxMonthOffset) {
			return;
		}
		this.monthOffset = newOffset;
		this.setDates();
		await this.reloadWorkload(this.selectedDivision, this.selectedFamilyGroup);
	}

	isMonthArrowDisabled(delta: number): boolean {
		return this.loading || (delta < 0 ? this.monthOffset === 0 : this.monthOffset >= this.maxMonthOffset);
	}

	onMonthArrowClick(delta: number) {
		if (this.isMonthArrowDisabled(delta)) {
			this.shakingArrow = delta < 0 ? "previous" : "next";
			setTimeout(() => (this.shakingArrow = null), 280);
			return;
		}

		this.changeMonth(delta);
	}

	async getBusinessUnitOptions() {
		this.businessUnitOptions = (
			this.isAdmin || this.isAdminOrGBL
				? await this.customizingService.getBusinessUnitAllowlistEmployees()
				: await this._employeeService.getBusinessUnits(false)
		)
			.sort()
			.map((businessUnit) => ({
				value: businessUnit,
				viewValue: MarketUnit.MarketUnit[businessUnit],
			}))
			.filter((entry) => entry.viewValue !== undefined);
	}

	async getDivisionOptions(selectedBusinessUnit: string, changedBusinessUnit: boolean) {
		this.divisionOptions = await this.divisionService.getDivisionOptionsByBusinessUnit([selectedBusinessUnit]);
		// when there are no divisions for the BU the division service returns all divisions
		// if so we check against the size of all divisions and set options to none except 'Alle Bereiche'
		if (this.divisionOptions.length === this.divisionService.divisionCache.size) {
			this.divisionOptions = [];
		}
		if (this.canViewCockpit) {
			this.divisionOptions.push({value: "All", viewValue: "Alle Bereiche"});
		}
		if (changedBusinessUnit) {
			this.setVariablesAfterBUChange();
			this.putPreferences();
		} else {
			this.noDivision = false;
		}
	}

	private setVariablesAfterBUChange() {
		this.noDivision = true;
		this.selectedDivision = "";
		this.selectedDivisionLabel = "";
		this.MEPWorkloadPreviousMonth = 0;
		this.MEPWorkloadCMEO = 0;
		this.MEPWorkloadCMSO = 0;
		this.MEPWorkloadNMEO = 0;
		this.MEPWorkloadNMSO = 0;
		this.MECWorkload = 0;
		this.MECQuality = 0;
	}

	private getMECData(selectedBusinessUnit, selectedDivision, selectedFamilyGroup, bookingQuality): Observable<number> {
		if (this.selectedDivision === "All") {
			return this.homescreenService.getMECData(selectedBusinessUnit, selectedFamilyGroup, bookingQuality, true);
		} else {
			return this.homescreenService.getMECData(selectedDivision, selectedFamilyGroup, bookingQuality, false);
		}
	}

	private putPreferences() {
		this.preferencesService.put(Preference.HOMESCREEN_BUSINESS_UNIT, this.selectedBusinessUnit, "homescreen").then();
		this.preferencesService.put(Preference.HOMESCREEN_DIVISION, this.selectedDivision, "homescreen").then();
		this.preferencesService.put(Preference.HOMESCREEN_VIEW_TYPE, this.selectedFamilyGroup, "homescreen").then();
	}

	private getPreferences(user: ApplicationUser) {
		this.preferencesService.get(Preference.HOMESCREEN_BUSINESS_UNIT, "homescreen").then((val) => {
			if (val && Object.keys(MarketUnit.MarketUnit).includes(val)) {
				this.selectedBusinessUnit = val;
			} else {
				if (val) {
					this.preferencesService.remove(Preference.HOMESCREEN_BUSINESS_UNIT, "homescreen").then();
				}
				this.selectedBusinessUnit = user.employee.businessUnit;
			}
		});

		this.preferencesService.get(Preference.HOMESCREEN_DIVISION, "homescreen").then((val) => {
			if (val) {
				this.selectedDivision = val;
			} else {
				this.selectedDivision = user.employee.division;
				this.selectedBusinessUnit = user.employee.businessUnit;
			}
		});

		this.preferencesService.get(Preference.HOMESCREEN_VIEW_TYPE, "homescreen").then((val) => {
			val ? (this.selectedFamilyGroup = val) : (this.selectedFamilyGroup = "M_B");
		});
	}

	private isWorthy() {
		return (
			this.isDivisionManager ||
			this.loginService.isDepartmentManager() ||
			this.isAdminOrGBL ||
			this.loginService.isBusinessUnitManager() ||
			this.loginService.isBusinessUnitLeader() ||
			this.loginService.isExecutive() ||
			this.isAdmin
		);
	}

	getUserRoles() {
		this.departmentManager = this.loginService.isDepartmentManager();
		this.isDivisionManager = this.loginService.isDivisionManager();
		this.canViewCockpit =
			this.loginService.isMepAdmin() ||
			this.loginService.isBusinessUnitManager() ||
			this.loginService.isExecutive() ||
			this.loginService.isDivisionManager() ||
			this.loginService.isDepartmentManager();
		this.isAdmin = this.loginService.isMepAdmin();
	}

	getLastImportDate() {
		return this.homescreenService.getLastImportDate();
	}
}
